import {
  Box,
  DeleteIcon,
  GKButton,
  GKCheckbox,
  GKTextField,
  IconButton,
  PlusIcon,
  RupeeIcon,
  Typography,
} from "components";
import { useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { moneyCommaSeparator } from "utils";
import type { MFOrderFormValues, MFOrderItem } from "./useMFOrders";

const round = (value: number, dp: number) => {
  const f = 10 ** dp;
  return Math.round(value * f) / f;
};

export interface FolioWithHolding {
  folioNumber: string;
  schemeHolding: number;
  holdingQty: number;
  investedAmount?: number;
  nav?: number;
  isSipActive?: boolean;
}

export interface PortfolioWithFolios {
  portfolioId: number;
  clientName: string;
  portfolioCode: string;
  portfolioValue: number;
  folios: FolioWithHolding[];
}

interface Props {
  portfolios: PortfolioWithFolios[];
  onAddFolio?: (portfolioId: number) => void;
  onDeleteFolio?: (itemIndex: number) => void;
  readOnly?: boolean;
}

const formatAmount = (value: number | null | undefined) =>
  value != null && value !== 0 ? `₹${moneyCommaSeparator(value)}` : "-";

const formatUnits = (value: number | null | undefined) =>
  value != null && value !== 0 ? moneyCommaSeparator(value) : "-";

const CELL_PADDING = "12px";

const COL_FLEX = {
  folio: 1.5,
  holding: 1.5,
  input: 2,
  all: 1,
};

const ColumnHeader = ({
  label,
  required,
  align = "left",
  flex = 1,
}: {
  label: string;
  required?: boolean;
  align?: "left" | "right";
  flex?: number;
}) => (
  <Box
    sx={{
      alignItems: "center",
      borderLeft: "1px solid var(--neutral-100)",
      display: "flex",
      flex,
      justifyContent: align === "right" ? "flex-end" : "flex-start",
      minWidth: 0,
      p: CELL_PADDING,
      "&:first-of-type": { borderLeft: 0 },
    }}
  >
    <Typography
      color="var(--black-900)"
      fontSize={12}
      fontWeight={600}
      sx={{
        letterSpacing: "0.6px",
        textAlign: align,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {label}
      {required && (
        <Typography
          component="span"
          color="var(--warning-700)"
          fontSize={12}
          fontWeight={600}
        >
          *
        </Typography>
      )}
    </Typography>
  </Box>
);

const ReadOnlyCell = ({
  value,
  align = "left",
  flex = 1,
}: {
  value: string;
  align?: "left" | "right";
  flex?: number;
}) => (
  <Box
    sx={{
      alignItems: "center",
      borderLeft: "1px solid var(--neutral-100)",
      display: "flex",
      flex,
      height: "100%",
      justifyContent: align === "right" ? "flex-end" : "flex-start",
      minWidth: 0,
      px: "12px",
      py: "12px",
      "&:first-of-type": { borderLeft: 0 },
    }}
  >
    <Typography
      color="var(--black-900)"
      fontSize={14}
      fontWeight={500}
      sx={{
        overflow: "hidden",
        textAlign: align,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const InputCell = ({
  name,
  placeholder,
  endAdornment,
  disabled,
  flex = 1,
  onValueChange,
  allowDecimal = true,
}: {
  name: `items.${number}.amount` | `items.${number}.units`;
  placeholder: string;
  endAdornment?: React.ReactNode;
  disabled?: boolean;
  flex?: number;
  onValueChange?: (value: number | null) => void;
  allowDecimal?: boolean;
}) => {
  const form = useFormContext<MFOrderFormValues>();
  return (
    <Box
      sx={{
        alignItems: "center",
        borderLeft: "1px solid var(--neutral-100)",
        display: "flex",
        flex,
        minWidth: 0,
        px: "12px",
        py: "10px",
        "&:first-of-type": { borderLeft: 0 },
      }}
    >
      <Controller
        control={form.control}
        name={name}
        render={({ field }) => (
          <GKTextField
            {...field}
            value={field.value ?? ""}
            placeholder={placeholder}
            disabled={disabled}
            type="number"
            onKeyDown={(e) => {
              if (["e", "E", "+", "-"].includes(e.key)) {
                e.preventDefault();
              }
              if (!allowDecimal && e.key === ".") {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const v = e.target.value;
              let next = v === "" ? null : Number(v);
              if (next != null && !allowDecimal) {
                next = Math.trunc(next);
              }
              field.onChange(next);
              onValueChange?.(next);
            }}
            slotProps={{
              input: { endAdornment },
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                height: 40,
              },
              "& .MuiOutlinedInput-input": {
                px: "12px",
                py: "8px",
              },
            }}
          />
        )}
      />
    </Box>
  );
};

const PortfolioHeader = ({
  clientName,
  portfolioCode,
  portfolioValue,
  onAddFolio,
  showAddFolio = true,
}: {
  clientName: string;
  portfolioCode: string;
  portfolioValue: number;
  onAddFolio?: () => void;
  showAddFolio?: boolean;
}) => (
  <Box
    sx={{
      alignItems: "center",
      bgcolor: "var(--white)",
      display: "flex",
      gap: "10px",
      height: 56,
      px: "12px",
      py: "8px",
      width: "100%",
    }}
  >
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        flex: 1,
        gap: "16px",
        minWidth: 0,
      }}
    >
      <Box sx={{ alignItems: "center", display: "flex", gap: "10px" }}>
        <Typography color="var(--black-900)" fontSize={14} fontWeight={500}>
          {clientName}
        </Typography>
        <Box
          sx={{
            bgcolor: "var(--grey-500)",
            borderRadius: "50%",
            height: 4,
            width: 4,
          }}
        />
        <Typography color="var(--grey-500)" fontSize={12} fontWeight={500}>
          {portfolioCode}
        </Typography>
      </Box>
      <Box
        sx={{
          bgcolor: "var(--neutral-100)",
          height: 16,
          width: "1px",
        }}
      />
      <Box sx={{ alignItems: "center", display: "flex", gap: "4px" }}>
        <Typography color="var(--neutral-500)" fontSize={12} fontWeight={500}>
          Portfolio Value:
        </Typography>
        <Typography color="var(--neutral-700)" fontSize={12} fontWeight={600}>
          ₹{moneyCommaSeparator(portfolioValue)}
        </Typography>
      </Box>
    </Box>
    {showAddFolio && (
      <GKButton
        variant="text"
        startIcon={<PlusIcon height={16} width={16} />}
        onClick={onAddFolio}
        sx={{
          color: "var(--primary-900)",
          fontSize: 12,
          fontWeight: 600,
          height: 40,
          p: "12px",
          textTransform: "none",
          "&:hover": { bgcolor: "transparent" },
        }}
      >
        Add New Folio
      </GKButton>
    )}
  </Box>
);

const SipActivePill = () => (
  <Box
    sx={{
      alignItems: "center",
      bgcolor: "#e8f3ec",
      border: "1px solid var(--white)",
      borderRadius: "6px",
      display: "inline-flex",
      ml: "8px",
      px: "12px",
      py: "6px",
    }}
  >
    <Typography
      color="#15803d"
      fontSize={10}
      fontWeight={600}
      sx={{ letterSpacing: "0.3px", textTransform: "uppercase" }}
    >
      SIP Active
    </Typography>
  </Box>
);

const EnterAmountFolioTable = (props: Props) => {
  const { portfolios, onAddFolio, onDeleteFolio, readOnly = false } = props;

  // Scheme-level NAV (same for all folios of a scheme) — used to convert
  // amount <-> units for fresh purchase folios that have no holding row.
  const schemeNav =
    portfolios
      .flatMap((portfolio) => portfolio.folios)
      .find((folio) => folio.nav > 0)?.nav || 0;

  const form = useFormContext<MFOrderFormValues>();
  // Tracks which field the user is editing per folio so the other can be
  // auto-calculated (via NAV) and shown disabled.
  const [driver, setDriver] = useState<
    Record<string, "amount" | "units" | undefined>
  >({});
  const items = useWatch({ control: form.control, name: "items" }) || [];
  const transactionType = useWatch({
    control: form.control,
    name: "transactionType",
  });
  const orderType = useWatch({
    control: form.control,
    name: "orderType",
  });
  const isSip = transactionType === "sip";
  const isSwp = transactionType === "swp";
  const isStp = transactionType === "stp";
  const isPlan = isSip || isSwp || isStp;
  const isRedemption = orderType === "redemption";
  const isSwitch = orderType === "switch";
  const isHoldingsBased = isRedemption || isSwitch;

  const amountColumnLabel = isSwitch
    ? readOnly
      ? "Switch Amount"
      : "Switch Amount(₹)"
    : isRedemption
      ? readOnly && isSwp
        ? "Redemption Amount(₹)"
        : isSwp
          ? "Redeem Amount(₹)"
          : "Redeem Amount (₹)"
      : isSip
        ? "SIP Amount"
        : isStp
          ? "STP Amount"
          : "Purchase Amount";
  const unitsColumnLabel = isSwitch
    ? "Switch QTY/Units"
    : isRedemption
      ? "Redeem QTY/Units"
      : "Purchase QTY/Units";
  const holdingAmountLabel = isHoldingsBased
    ? "Total Amount (₹)"
    : "Scheme Holding";
  const holdingQtyLabel = isHoldingsBased
    ? "Total QTY/Units"
    : "Holding QTY/unit";
  const allColumnLabel = isSwitch ? "Switch All" : "Redeem All";

  const hideHoldingAmount = readOnly && isHoldingsBased && !isPlan;
  const hideHoldingQty = readOnly && isHoldingsBased;

  const findItemIndex = (portfolioId: number, folioNumber: string) =>
    items.findIndex(
      (item: MFOrderItem) =>
        item.portfolioId === portfolioId && item.folioNumber === folioNumber,
    );

  const handleAllToggle = (
    idx: number,
    checked: boolean,
    holdingAmount: number,
    holdingQty: number,
    driverKey: string,
  ) => {
    setDriver((d) => ({ ...d, [driverKey]: undefined }));
    if (isSwitch) {
      form.setValue(`items.${idx}.switchType`, checked ? "all" : null, {
        shouldDirty: true,
      });
    } else {
      form.setValue(`items.${idx}.redemptionType`, checked ? "all" : null, {
        shouldDirty: true,
      });
    }
    // "All" fills both fields with the full holding for display; the payload
    // sends units only (driven by redemptionType/switchType === "all").
    form.setValue(`items.${idx}.entryMode`, null, { shouldDirty: true });
    form.setValue(`items.${idx}.amount`, checked ? holdingAmount : null, {
      shouldDirty: true,
    });
    form.setValue(`items.${idx}.units`, checked ? holdingQty : null, {
      shouldDirty: true,
    });
  };

  // NAV-based two-way conversion: editing Amount fills Units (= amount / NAV)
  // and editing Units fills Amount (= units * NAV); the calculated field is
  // shown disabled. Clearing the edited field clears both.
  const handleConvert = (
    idx: number,
    driverKey: string,
    field: "amount" | "units",
    value: number | null,
    nav: number,
    maxAmount: number,
    maxQty: number,
  ) => {
    if (value == null) {
      setDriver((d) => ({ ...d, [driverKey]: undefined }));
      form.setValue(`items.${idx}.entryMode`, null, { shouldDirty: true });
      form.setValue(`items.${idx}.amount`, null, { shouldDirty: true });
      form.setValue(`items.${idx}.units`, null, { shouldDirty: true });
      return;
    }
    setDriver((d) => ({ ...d, [driverKey]: field }));
    form.setValue(`items.${idx}.entryMode`, field, { shouldDirty: true });
    if (field === "amount") {
      // Cannot redeem/switch more than the holding's total value.
      const capped = maxAmount > 0 ? Math.min(value, maxAmount) : value;
      form.setValue(`items.${idx}.amount`, round(capped, 2), {
        shouldDirty: true,
      });
      if (nav > 0) {
        form.setValue(`items.${idx}.units`, round(capped / nav, 4), {
          shouldDirty: true,
        });
      }
    } else {
      // Cannot redeem/switch more than the available units.
      const capped = maxQty > 0 ? Math.min(value, maxQty) : value;
      form.setValue(`items.${idx}.units`, round(capped, 4), {
        shouldDirty: true,
      });
      if (nav > 0) {
        // Amount is whole-rupees only, so round the units→amount result up.
        form.setValue(`items.${idx}.amount`, Math.ceil(capped * nav), {
          shouldDirty: true,
        });
      }
    }
  };

  if (!portfolios?.length) return null;

  return (
    <Box
      sx={{
        border: "1px solid var(--neutral-100)",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {portfolios.map((portfolio, portfolioIdx) => (
        <Box key={portfolio.portfolioId} sx={{ width: "100%" }}>
          <PortfolioHeader
            clientName={portfolio.clientName}
            portfolioCode={portfolio.portfolioCode}
            portfolioValue={portfolio.portfolioValue}
            onAddFolio={() => onAddFolio?.(portfolio.portfolioId)}
            showAddFolio={!readOnly && !isHoldingsBased}
          />

          <Box
            sx={{
              alignItems: "center",
              bgcolor: "var(--neutral-50)",
              borderBottom: "1px solid var(--neutral-100)",
              borderTop: "1px solid var(--neutral-100)",
              display: "flex",
              width: "100%",
            }}
          >
            <ColumnHeader label="Folio No." flex={COL_FLEX.folio} />
            {!hideHoldingAmount && (
              <ColumnHeader
                label={holdingAmountLabel}
                align="right"
                flex={COL_FLEX.holding}
              />
            )}
            {!hideHoldingQty && (
              <ColumnHeader
                label={holdingQtyLabel}
                align="right"
                flex={COL_FLEX.holding}
              />
            )}
            <ColumnHeader
              label={amountColumnLabel}
              required={!readOnly}
              align={readOnly ? "right" : "left"}
              flex={COL_FLEX.input}
            />
            {!isPlan && (
              <ColumnHeader
                label={unitsColumnLabel}
                required={!readOnly}
                align={readOnly ? "right" : "left"}
                flex={COL_FLEX.input}
              />
            )}
            {isHoldingsBased && !isPlan && !readOnly && (
              <ColumnHeader
                label={allColumnLabel}
                align="right"
                flex={COL_FLEX.all}
              />
            )}
          </Box>

          {!isHoldingsBased &&
            items
              .map((item, idx) => ({ item, idx }))
              .filter(
                ({ item }) =>
                  item.portfolioId === portfolio.portfolioId &&
                  !item.folioNumber,
              )
              .map(({ idx }, newFolioIndex) => {
                const amountFilled =
                  items[idx]?.amount != null && items[idx]?.amount !== 0;
                const unitsFilled =
                  items[idx]?.units != null && items[idx]?.units !== 0;
                const driverKey = `${portfolio.portfolioId}-new-${idx}`;
                const rowDriver = driver[driverKey];
                const conversionEnabled = !isPlan && !readOnly && schemeNav > 0;
                return (
                  <Box
                    key={`new-${idx}`}
                    sx={{
                      alignItems: "center",
                      bgcolor: "var(--grey-bg-100, #f8f8f8)",
                      borderBottom: "1px solid var(--neutral-100)",
                      display: "flex",
                      height: 61,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        alignItems: "center",
                        display: "flex",
                        flex: COL_FLEX.folio,
                        gap: "4px",
                        height: "100%",
                        justifyContent: "space-between",
                        minWidth: 0,
                        px: "12px",
                        py: "12px",
                      }}
                    >
                      <Typography
                        color="var(--black-900)"
                        fontSize={14}
                        fontStyle="italic"
                        fontWeight={500}
                      >
                        New Folio {newFolioIndex + 1}
                      </Typography>
                      {!readOnly && onDeleteFolio && (
                        <IconButton
                          size="small"
                          onClick={() => onDeleteFolio(idx)}
                          sx={{ p: "4px" }}
                          aria-label="Remove folio"
                        >
                          <DeleteIcon
                            height={16}
                            width={16}
                            color="var(--error-700, #c35245)"
                          />
                        </IconButton>
                      )}
                    </Box>
                    {!hideHoldingAmount && (
                      <ReadOnlyCell
                        value="-"
                        align="right"
                        flex={COL_FLEX.holding}
                      />
                    )}
                    {!hideHoldingQty && (
                      <ReadOnlyCell
                        value="-"
                        align="right"
                        flex={COL_FLEX.holding}
                      />
                    )}
                    {readOnly ? (
                      <ReadOnlyCell
                        value={formatAmount(items[idx]?.amount)}
                        align="right"
                        flex={COL_FLEX.input}
                      />
                    ) : (
                      <InputCell
                        name={`items.${idx}.amount`}
                        placeholder="Amount"
                        allowDecimal={false}
                        endAdornment={<RupeeIcon height={16} width={16} />}
                        disabled={
                          conversionEnabled
                            ? rowDriver === "units"
                            : !isPlan && unitsFilled
                        }
                        flex={COL_FLEX.input}
                        onValueChange={
                          conversionEnabled
                            ? (val) =>
                                handleConvert(
                                  idx,
                                  driverKey,
                                  "amount",
                                  val,
                                  schemeNav,
                                  0,
                                  0,
                                )
                            : undefined
                        }
                      />
                    )}
                    {!isPlan &&
                      (readOnly ? (
                        <ReadOnlyCell
                          value={formatUnits(items[idx]?.units)}
                          align="right"
                          flex={COL_FLEX.input}
                        />
                      ) : (
                        <InputCell
                          name={`items.${idx}.units`}
                          placeholder="QTY/Units"
                          disabled={
                            conversionEnabled
                              ? rowDriver === "amount"
                              : amountFilled
                          }
                          flex={COL_FLEX.input}
                          onValueChange={
                            conversionEnabled
                              ? (val) =>
                                  handleConvert(
                                    idx,
                                    driverKey,
                                    "units",
                                    val,
                                    schemeNav,
                                    0,
                                    0,
                                  )
                              : undefined
                          }
                        />
                      ))}
                  </Box>
                );
              })}

          {portfolio.folios.map((folio) => {
            const idx = findItemIndex(portfolio.portfolioId, folio.folioNumber);
            const hasItem = idx >= 0;
            const amountFilled =
              hasItem && items[idx]?.amount != null && items[idx]?.amount !== 0;
            const unitsFilled =
              hasItem && items[idx]?.units != null && items[idx]?.units !== 0;
            const isAllSelected =
              hasItem &&
              (items[idx]?.redemptionType === "all" ||
                items[idx]?.switchType === "all");
            const navValue =
              folio.nav && folio.nav > 0
                ? folio.nav
                : folio.holdingQty > 0
                  ? folio.schemeHolding / folio.holdingQty
                  : 0;
            const driverKey = `${portfolio.portfolioId}-${folio.folioNumber}`;
            const rowDriver = driver[driverKey];
            const conversionEnabled = !isPlan && !readOnly && navValue > 0;
            // Holdings-based flows cap at the holding; purchase has no cap.
            const maxAmount = isHoldingsBased
              ? isRedemption
                ? folio.schemeHolding
                : (folio.investedAmount ?? 0)
              : 0;
            const maxQty = isHoldingsBased ? folio.holdingQty : 0;
            return (
              <Box
                key={folio.folioNumber}
                sx={{
                  alignItems: "center",
                  bgcolor: "var(--white)",
                  borderBottom: "1px solid var(--neutral-100)",
                  display: "flex",
                  height: 61,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    flex: COL_FLEX.folio,
                    height: "100%",
                    minWidth: 0,
                    px: "12px",
                    py: "12px",
                  }}
                >
                  <Typography
                    color="var(--black-900)"
                    fontSize={14}
                    fontWeight={500}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {folio.folioNumber}
                  </Typography>
                  {isSip && folio.isSipActive && <SipActivePill />}
                </Box>
                {!hideHoldingAmount && (
                  <ReadOnlyCell
                    value={
                      isRedemption
                        ? `₹${moneyCommaSeparator(folio.schemeHolding)}`
                        : `₹${moneyCommaSeparator(folio.investedAmount)}`
                    }
                    align="right"
                    flex={COL_FLEX.holding}
                  />
                )}
                {!hideHoldingQty && (
                  <ReadOnlyCell
                    value={moneyCommaSeparator(folio.holdingQty)}
                    align="right"
                    flex={COL_FLEX.holding}
                  />
                )}
                {readOnly ? (
                  <ReadOnlyCell
                    value={formatAmount(items[idx]?.amount)}
                    align="right"
                    flex={COL_FLEX.input}
                  />
                ) : (
                  <InputCell
                    name={`items.${idx}.amount`}
                    placeholder="Amount"
                    allowDecimal={false}
                    endAdornment={<RupeeIcon height={16} width={16} />}
                    disabled={
                      !hasItem ||
                      (isHoldingsBased && isAllSelected) ||
                      (conversionEnabled
                        ? rowDriver === "units"
                        : !isPlan && unitsFilled)
                    }
                    flex={COL_FLEX.input}
                    onValueChange={
                      conversionEnabled
                        ? (val) =>
                            handleConvert(
                              idx,
                              driverKey,
                              "amount",
                              val,
                              navValue,
                              maxAmount,
                              maxQty,
                            )
                        : undefined
                    }
                  />
                )}
                {!isPlan &&
                  (readOnly ? (
                    <ReadOnlyCell
                      value={formatUnits(items[idx]?.units)}
                      align="right"
                      flex={COL_FLEX.input}
                    />
                  ) : (
                    <InputCell
                      name={`items.${idx}.units`}
                      placeholder="QTY/Units"
                      disabled={
                        !hasItem ||
                        (isHoldingsBased && isAllSelected) ||
                        (conversionEnabled
                          ? rowDriver === "amount"
                          : amountFilled)
                      }
                      flex={COL_FLEX.input}
                      onValueChange={
                        conversionEnabled
                          ? (val) =>
                              handleConvert(
                                idx,
                                driverKey,
                                "units",
                                val,
                                navValue,
                                maxAmount,
                                maxQty,
                              )
                          : undefined
                      }
                    />
                  ))}
                {isHoldingsBased && !isPlan && !readOnly && (
                  <Box
                    sx={{
                      alignItems: "center",
                      borderLeft: "1px solid var(--neutral-100)",
                      display: "flex",
                      flex: COL_FLEX.all,
                      height: "100%",
                      justifyContent: "center",
                      minWidth: 0,
                      px: "12px",
                    }}
                  >
                    <GKCheckbox
                      disabled={!hasItem}
                      checked={isAllSelected}
                      onChange={(e) =>
                        handleAllToggle(
                          idx,
                          e.target.checked,
                          isRedemption
                            ? folio.schemeHolding
                            : (folio.investedAmount ?? 0),
                          folio.holdingQty,
                          driverKey,
                        )
                      }
                    />
                  </Box>
                )}
              </Box>
            );
          })}

          {portfolioIdx < portfolios.length - 1 && (
            <Box
              sx={{
                bgcolor: "var(--grey-bg-100, #f8f8f8)",
                height: 8,
                width: "100%",
              }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default EnterAmountFolioTable;
